import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import chatService from "../services/chatService";
import authService from "../services/authService";

const DashboardNew = () => {
  const [user, setUser] = useState(null);
  const [families, setFamilies] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFamilyName, setNewFamilyName] = useState("");
  const [newConvTitle, setNewConvTitle] = useState("");
  const [newConvType, setNewConvType] = useState("couple");
  const [createFamilyOpen, setCreateFamilyOpen] = useState(false);
  const [createConvOpen, setCreateConvOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      console.log('🔄 Carregando dados do dashboard...');
      
      // Verificar se o usuário está autenticado
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Carregar dados do usuário (simulado por enquanto)
      setUser({ name: "Usuário Teste", email: "usuario@teste.com" });
      
      // Carregar famílias (simulado por enquanto)
      setFamilies([
        { id: 1, name: "Família Silva" },
        { id: 2, name: "Família Santos" }
      ]);

      // Carregar sessões de chat reais
      const sessions = await chatService.getSessions({ status: 'all', limit: 20 });
      const formattedConversations = sessions.map(session => 
        chatService.formatSessionForDisplay(session)
      );
      setConversations(formattedConversations);
      
      console.log('✅ Dados carregados:', { sessions: formattedConversations });
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      // Em caso de erro, manter dados simulados para não quebrar a interface
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    navigate("/");
  };

  const handleCreateFamily = () => {
    if (newFamilyName.trim()) {
      const newFamily = {
        id: families.length + 1,
        name: newFamilyName
      };
      setFamilies([...families, newFamily]);
      setNewFamilyName("");
      setCreateFamilyOpen(false);
    }
  };

  const handleCreateConversation = async () => {
    if (!newConvTitle.trim()) {
      console.warn('⚠️ Título da conversa não pode estar vazio');
      return;
    }
    
    try {
      console.log('🆕 Criando nova conversa...', { title: newConvTitle, type: newConvType });
      setLoading(true);
      
      // Criar nova sessão de chat com título customizado
      const sessionData = {
        title: newConvTitle.trim(), // Garantir que não há espaços extras
        type: newConvType,
        familyId: families[0]?.id || null
      };
      
      const newSession = await chatService.createSession(sessionData);
      
      // Atualizar lista de conversas
      await loadData();
      
      // Limpar formulário e fechar modal
      setNewConvTitle("");
      setCreateConvOpen(false);
      
      console.log('✅ Conversa criada com sucesso:', newSession);
      
      // Navegar para a conversa criada
      if (newSession.sessionId) {
        navigate(`/chat/${newSession.sessionId}`);
      }
      
    } catch (error) {
      console.error('❌ Erro ao criar conversa:', error);
      // Manter modal aberto em caso de erro para o usuário tentar novamente
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConversation = (conversation) => {
    console.log('🔗 Abrindo conversa:', conversation.id, conversation.title);
    // Navegar para a página de chat específica
    navigate(`/chat/${conversation.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <header className="border-b bg-white/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary rounded-full"></div>
            <h1 className="text-xl font-bold">Casal Resolve</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/conflicts")}>
              Conflitos
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/suggestions")}>
              Sugestões
            </Button>
            <Button variant="ghost" onClick={handleSignOut}>
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Olá, {user?.name || "Bem-vindo"}!</h2>
          <p className="text-muted-foreground">Gerencie suas conversas e família</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Families Card */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-primary rounded-full"></div>
                    Minhas Famílias
                  </CardTitle>
                  <CardDescription>Gerencie os membros da sua família</CardDescription>
                </div>
                <Dialog open={createFamilyOpen} onOpenChange={setCreateFamilyOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      Nova Família
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Criar Nova Família</DialogTitle>
                      <DialogDescription>
                        Crie um grupo familiar para gerenciar conversas
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="family-name">Nome da Família</Label>
                        <Input
                          id="family-name"
                          value={newFamilyName}
                          onChange={(e) => setNewFamilyName(e.target.value)}
                          placeholder="Ex: Família Silva"
                          maxLength={100}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {newFamilyName.length}/100 caracteres
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleCreateFamily}>Criar Família</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {families.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nenhuma família criada ainda
                </p>
              ) : (
                <div className="space-y-2">
                  {families.map((family) => (
                    <div key={family.id} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <span className="font-medium">{family.name}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/family/${family.id}`)}
                      >
                        Ver
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-5 h-5 bg-accent rounded-full"></div>
                Ações Rápidas
              </CardTitle>
              <CardDescription>Acesse rapidamente as funcionalidades</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/conflicts/new")}
                >
                  Reportar Novo Conflito
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/suggestions")}
                >
                  Ver Sugestões de Melhoria
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/analytics")}
                >
                  Visualizar Relatórios
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Conversations */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-secondary rounded-full"></div>
                  Conversas Recentes
                </CardTitle>
                <CardDescription>Continue suas conversas ou inicie uma nova</CardDescription>
              </div>
              <Dialog open={createConvOpen} onOpenChange={setCreateConvOpen}>
                <DialogTrigger asChild>
                  <Button disabled={families.length === 0}>
                    Nova Conversa
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nova Conversa</DialogTitle>
                    <DialogDescription>
                      Inicie uma nova conversa com o assistente
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="conv-title">Título *</Label>
                      <Input
                        id="conv-title"
                        value={newConvTitle}
                        onChange={(e) => setNewConvTitle(e.target.value)}
                        placeholder="Ex: Melhoria na comunicação"
                        maxLength={200}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {newConvTitle.length}/200 caracteres
                      </p>
                      {newConvTitle.trim().length === 0 && newConvTitle.length > 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          O título não pode conter apenas espaços
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="conv-type">Tipo de Conversa</Label>
                      <Select value={newConvType} onValueChange={setNewConvType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="couple">Casal</SelectItem>
                          <SelectItem value="family">Família</SelectItem>
                          <SelectItem value="parent_child">Pais e Filhos</SelectItem>
                          <SelectItem value="personal">Pessoal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button 
                      onClick={handleCreateConversation}
                      disabled={!newConvTitle.trim() || loading}
                    >
                      {loading ? 'Criando...' : 'Criar'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {conversations.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Nenhuma conversa ainda. Crie uma família primeiro!
              </p>
            ) : (
              <div className="grid gap-3">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className="flex justify-between items-center p-4 bg-muted hover:bg-muted/80 rounded-lg cursor-pointer transition-colors"
                    onClick={() => handleOpenConversation(conv)}
                  >
                    <div>
                      <h3 className="font-medium">{conv.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {conv.family} • {conv.type}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent double click
                        handleOpenConversation(conv);
                      }}
                    >
                      Abrir
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DashboardNew;